import React, { useState, useEffect } from 'react';
import CookieConsent from 'react-cookie-consent';
import { Cookie, Settings, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

const CookieConsentBanner = () => {
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Load saved preferences
    const saved = localStorage.getItem('era_cookie_preferences');
    if (saved) {
      setPreferences(JSON.parse(saved));
    }
  }, []);

  const savePreferences = (prefs) => {
    setPreferences(prefs);
    localStorage.setItem('era_cookie_preferences', JSON.stringify(prefs));
    
    // Initialize/disable Google Analytics based on consent
    if (prefs.analytics && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    } else if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
  };

  const acceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true
    };
    savePreferences(allAccepted);
  };

  const acceptEssentialOnly = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      marketing: false
    };
    savePreferences(essentialOnly);
  };

  return (
    <>
      <CookieConsent
        location="bottom"
        buttonText="Accept All"
        declineButtonText="Essential Only"
        enableDeclineButton
        cookieName="era_cookie_consent"
        style={{
          background: 'linear-gradient(135deg, #1a2332 0%, #2a3442 100%)',
          padding: '20px',
          alignItems: 'center',
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.3)'
        }}
        buttonStyle={{
          background: '#F7931A',
          color: '#ffffff',
          fontSize: '14px',
          fontWeight: '600',
          padding: '12px 24px',
          borderRadius: '8px',
          border: 'none',
          cursor: 'pointer'
        }}
        declineButtonStyle={{
          background: 'transparent',
          color: '#ffffff',
          fontSize: '14px',
          fontWeight: '600',
          padding: '12px 24px',
          borderRadius: '8px',
          border: '2px solid #ffffff',
          cursor: 'pointer'
        }}
        expires={365}
        onAccept={acceptAll}
        onDecline={acceptEssentialOnly}
      >
        <div className="flex items-start space-x-4">
          <Cookie className="w-6 h-6 text-bitcoin-orange flex-shrink-0 mt-1" />
          <div className="flex-1">
            <h3 className="text-white font-semibold text-lg mb-2">
              We value your privacy
            </h3>
            <p className="text-gray-300 text-sm leading-relaxed mb-3">
              Era uses cookies to provide essential functionality and improve your experience. 
              We use <strong>essential cookies</strong> for login and preferences (always active), 
              and optional <strong>analytics cookies</strong> to understand how you use our platform.
            </p>
            <div className="flex items-center space-x-4 text-sm">
              <button
                onClick={() => setShowPreferences(!showPreferences)}
                className="text-bitcoin-orange hover:underline flex items-center space-x-1"
              >
                <Settings className="w-4 h-4" />
                <span>Manage Preferences</span>
              </button>
              <Link to="/privacy-policy" className="text-gray-300 hover:text-white hover:underline">
                Privacy Policy
              </Link>
              <Link to="/cookie-policy" className="text-gray-300 hover:text-white hover:underline">
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </CookieConsent>

      {/* Cookie Preferences Modal */}
      {showPreferences && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
                <Shield className="w-6 h-6 text-bitcoin-orange" />
                <span>Cookie Preferences</span>
              </h2>
              <button
                onClick={() => setShowPreferences(false)}
                className="text-gray-600 hover:text-gray-900"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* Essential Cookies */}
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-gray-900">Essential Cookies</h3>
                    <p className="text-sm text-gray-600">Required for login and core functionality</p>
                  </div>
                  <div className="text-sm font-semibold text-green-600">Always Active</div>
                </div>
                <p className="text-xs text-gray-600">
                  These cookies are necessary for the website to function and cannot be disabled.
                </p>
              </div>

              {/* Analytics Cookies */}
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-gray-900">Analytics Cookies</h3>
                    <p className="text-sm text-gray-600">Help us understand how you use Era</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-bitcoin-orange"></div>
                  </label>
                </div>
                <p className="text-xs text-gray-600">
                  Google Analytics: Tracks page views, user interactions, and platform usage to improve our service.
                </p>
              </div>

              {/* Marketing Cookies */}
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 opacity-50">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-gray-900">Marketing Cookies</h3>
                    <p className="text-sm text-gray-600">Not currently used</p>
                  </div>
                  <div className="text-sm font-semibold text-gray-500">Coming Soon</div>
                </div>
                <p className="text-xs text-gray-600">
                  Future: May be used for targeted advertising and retargeting campaigns.
                </p>
              </div>
            </div>

            <div className="mt-6 flex space-x-3">
              <button
                onClick={() => {
                  savePreferences(preferences);
                  setShowPreferences(false);
                }}
                className="flex-1 bg-bitcoin-orange hover:bg-orange-600 text-white font-semibold py-3 rounded-lg"
              >
                Save Preferences
              </button>
              <button
                onClick={() => setShowPreferences(false)}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsentBanner;
